import React, { useMemo, useState } from 'react';
import { arrow, autoUpdate, flip, FloatingArrow, FloatingFocusManager, FloatingPortal, offset, Placement, safePolygon, shift, useClick, useDismiss, useFloating, useHover, useInteractions, useRole, } from '@floating-ui/react';
import clsx from 'clsx';

export type CooltipProps = {
	content: React.ReactNode;
	placement?: Placement;
	visible?: boolean;
	onClose?: () => void;
	interaction?: 'hover' | 'click';
	className?: string;
	children: React.ReactNode;
}

export function Cooltip({
	content,
	placement,
	visible: controlledVisible,
	onClose,
	interaction = 'hover',
	className,
	children,
}: CooltipProps) {
	const [uncontrolledVisible, setUncontrolledVisible] = useState(false);
	const isControlled = controlledVisible !== undefined;
	const visible = isControlled ? controlledVisible : uncontrolledVisible;
	const arrowRef = React.useRef(null);

	const { refs, floatingStyles, context, middlewareData } = useFloating({
		open: visible,
		onOpenChange: (open) => {
			if (!isControlled) {
				setUncontrolledVisible(open);
			}
			if (!open) {
				onClose?.();
			}
		},
		placement: placement ?? 'left-start',
		middleware: [
			offset({ mainAxis: 16 }),
			shift({ padding: 5 }),
			flip(),
			arrow({ element: arrowRef, padding: 12 }),
		],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context, { enabled: interaction === 'click' });
	const dismiss = useDismiss(context);
	const role = useRole(context);
	const hover = useHover(context, {
		handleClose: safePolygon(),
		enabled: interaction === 'hover',
	});

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role,
		hover,
	]);

	return (
		<>
			<div
				ref={refs.setReference}
				{...getReferenceProps()}
				className="inline-flex items-center justify-center cursor-pointer"
			>
				{children}
			</div>
			{visible && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={floatingStyles}
						{...getFloatingProps()}
						className={clsx('box-border w-[300px] bg-white rounded-lg p-6 shadow-lg border border-borders-primary z-[var(--z-tooltip)] outline-none', className)}
					>
						<FloatingArrow
							ref={arrowRef}
							context={context}
							width={12}
							height={9}
							strokeWidth={1}
							className="fill-white [&>path:first-of-type]:stroke-borders-primary [&>path:last-of-type]:stroke-white"
						/>
						{content}
					</div>
				</FloatingPortal>
			)}
		</>
	);
}
